import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import type { ItemTypeOption } from 'src/lib/api/types/loop-visualization';
import type { ItemV2 } from '@/lib/api/types/items';
import type { TimelineItemType } from './useTimelineTableColumns';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';

export const useTimelineItemsWithChildren = ({
  from,
  to,
  items,
  targetLevelItemTypes,
  nextLevelItemTypes,
  showChildItems,
}: {
  from: string | null;
  to: string | null;
  items: ItemV2[];
  targetLevelItemTypes: ItemTypeOption[];
  nextLevelItemTypes: ItemTypeOption[];
  showChildItems: boolean;
}): TimelineItemType[] => {
  return useMemo<TimelineItemType[]>(() => {
    if (!from || !to) {
      return [];
    }
    const enrichedItems = items.map((item) => ({
      ...item,
      isEditable: true,
    }));

    const targetLevelItemTypeIds = new Set(targetLevelItemTypes.map((t) => t.id));
    const targetRows = enrichedItems.filter((item) => targetLevelItemTypeIds.has(item.itemTypeId));

    if (!showChildItems) {
      return targetRows;
    }

    const nextLevelItemTypeIds = new Set(nextLevelItemTypes.map((t) => t.id));
    const childItemsByParentId = enrichedItems.reduce(
      (obj, item) => {
        const childDates = getNormalizePlannedDates(
          item.plannedStartDateTime,
          item.plannedFinishDateTime,
        );
        if (
          !item.parentId ||
          !childDates.hasAnyDate ||
          !childDates.start ||
          !childDates.finish ||
          isBefore(to, childDates.start) ||
          isBefore(childDates.finish, from) ||
          !nextLevelItemTypeIds.has(item.itemTypeId)
        ) {
          return obj;
        }

        if (!obj[item.parentId]) {
          obj[item.parentId] = [];
        }
        obj[item.parentId]?.push(item);
        return obj;
      },
      {} as Record<number, TimelineItemType[]>,
    );

    return targetRows.map((item) => ({
      ...item,
      children: childItemsByParentId[item.id] ?? [],
    }));
  }, [from, to, items, targetLevelItemTypes, nextLevelItemTypes, showChildItems]);
};
